import * as React from 'react';
import * as _ from 'lodash';
import { Route, withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import { BulkUpload, BulkUploadHistory } from '@vcd-ecm-ui/BulkUpload';
import {
  CertificateSettings,
  CustomerSettings,
  TaxPayer,
  TaxPayers,
  useCertificateSettings,
  useCustomerSettings,
} from '@vcd-ecm-ui/Settings';
import {
  Settings,
  Sellers,
  Seller,
  Products,
  Product,
  ExemptionTypes,
  Jurisdictions,
} from '@vcd-ecm-ui/ECWizard';
import { CustomerGrid } from '@vcd-ecm-ui/CustomerGrid';
import {
  AuditReport,
  AuditReports,
  CertificateReport,
  CertificateReports,
  ErrorLog,
} from '@vcd-ecm-ui/Reports';
import {
  ConfigurationStatus,
  ConfigurationSteps,
  Subscriptions,
  Templates,
} from '@vcd-ecm-ui/Correspondence';
import { CustomerInfo } from '@vcd-ecm-ui/CustomerInfo';
import { CertificateGrid } from '@vcd-ecm-ui/CertificateGrid';
import {
  Logging,
  UserContext,
  SettingsContextObject,
  SettingsContext,
  ContentLoading,
  Notification,
  PermissionGroup,
  getCreatePermission,
  getViewPermission,
  getEditPermission,
} from '@vcd-ecm-ui/Common';
import { GATEWAY_ENDPOINT } from '../../constants/constants';
import { uploadAdd } from '../../hooks/upload/state/upload-hook-actions';
import useUser from '../../hooks/user/useUser';
import useUpload from '../../hooks/upload/useUpload';
import { AddCertificate } from '../../../../packages/reports/node_modules/@vcd-ecm-ui/SideBySideViewer/src';
import useLoginGatekeeper from '../../hooks/useLoginGatekeeper';

const idleTimeoutMinutes = 8 * 60; // 8 Hours
const logoutOnIdle = true;

const versionItem = process.env.BUILD !== 'production'
  && process.env.VERSION
  && {
    id: process.env.VERSION,
    Component: (): JSX.Element => <span>VERSION {process.env.VERSION}</span>,
  };

const additionalLegalItems = [
  versionItem,
].filter((item) => !!item);

const Content = React.memo((): JSX.Element => {
  const dispatch = useDispatch();
  const [userInfo] = useUser();
  const [uploads] = useUpload();
  const [notification, setNotification] = React.useState(null);
  const [, , authUrls] = useLoginGatekeeper();

  const initialSettings = (): SettingsContext => {
    const settings: SettingsContext = {
      certificates: {},
      customers: {},
      ecWizard: {},
    };

    return settings;
  };

  // Load the Certificate Settings - Settings will be cached to the Session Storage for later use
  const [certificateSettings, certificateSettingsObject, certificateSettingsNotification] = useCertificateSettings(GATEWAY_ENDPOINT);

  // Load the Customer Settings - Settings will be cached to the Session Storage for later use
  const [customerSettings, customerSettingsObject, customerSettingsNotification] = useCustomerSettings(GATEWAY_ENDPOINT);

  // The Settings Context
  const [settingsContext, setSettingsContext] = React.useState(initialSettings());

  // Provider Value will Change when settingsContext or setSettingsContext changes
  const settingsContextProviderValue = React.useMemo(
    () => ({ settingsContext, setSettingsContext }), [settingsContext, setSettingsContext],
  );

  // Add the S3 Upload to the Queue
  const uploadFilesToS3 = (uuid: string, files: File[], fileNameUrlMap: Map<string, string>) => {
    dispatch(uploadAdd(uuid, files, fileNameUrlMap));
  };

  const handleOnIdle = (event: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (logoutOnIdle) {
      authUrls.logoutFunction();
    }
  };

  // https://www.npmjs.com/package/react-idle-timer
  const {
    pause,
    resume,
  } = useIdleTimer({
    timeout: 1000 * 60 * idleTimeoutMinutes,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  // Clear the Notification Message
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  const reLogin = (): void => {
    authUrls.logoutFunction();
  };

  // Create a fail save Logout Timer - If the Token is successfully updated we should never have to Logout
  React.useEffect(() => {
    // Pause the Idle Timer when Uploads are in Progress
    if (uploads && uploads.length) {
      Logging.Info('Pausing Idle Timer');
      pause();
    } else { // Resume the Idle Timer when Uploads are in Progress
      Logging.Info('Resuming Idle Timer');
      resume();
    }
  }, [uploads]);

  // Certificate Settings was loaded.   Store the Processed Status in the Certificate Settings Context
  React.useEffect(() => {
    if (certificateSettingsObject) {
      const settings = _.cloneDeep(settingsContext);
      settings.certificates = certificateSettingsObject;

      setSettingsContext(settings);
    }
  }, [certificateSettingsObject]);

  // Customer Settings was loaded.   Store the Processed Status in the Customer Settings Context
  React.useEffect(() => {
    if (customerSettingsObject) {
      const settings = _.cloneDeep(settingsContext);
      settings.customers = customerSettingsObject;

      setSettingsContext(settings);
    }
  }, [customerSettingsObject]);

  React.useEffect(() => {
    if (certificateSettingsNotification) {
      // 401 Unauthorized, Trigger a logout
      if (certificateSettingsNotification.error?.statusCode === 401) {
        reLogin();
      } else {
        setNotification(certificateSettingsNotification);
      }
    }
  }, [certificateSettingsNotification]);

  React.useEffect(() => {
    if (customerSettingsNotification) {
      // 401 Unautorized, Trigger a logout
      if (customerSettingsNotification.error?.statusCode === 401) {
        reLogin();
      } else {
        setNotification(customerSettingsNotification);
      }
    }
  }, [customerSettingsNotification]);

  // Wait Until the User Info and Settings are loaded
  if (!userInfo || !certificateSettings || !certificateSettingsObject || !customerSettings || !customerSettingsObject) {
    return (
      <div className="content__wrapper">
        {
          (!certificateSettingsNotification || !customerSettingsNotification)
          && (
            <ContentLoading loadingMessage="Loading Initial Data ..." />
          )
        }
        <Notification
          data={notification}
          onNotificationClose={onNotificationClose}
          autoClose
        />
      </div>
    );
  }

  const username = userInfo && userInfo.username;
  const permissionCertificatesView: boolean = getViewPermission(userInfo, PermissionGroup.Certificates);
  const permissionBulkUploadView: boolean = getViewPermission(userInfo, PermissionGroup.BulkUpload);
  const permissionCustomersView: boolean = getViewPermission(userInfo, PermissionGroup.Customers);
  const permissionSettingsView: boolean = getViewPermission(userInfo, PermissionGroup.Settings);
  const permissionSettingsCreate: boolean = getCreatePermission(userInfo, PermissionGroup.Settings);
  const permissionSettingsEdit: boolean = getEditPermission(userInfo, PermissionGroup.Settings);
  const permissionReportsView: boolean = getViewPermission(userInfo, PermissionGroup.Reports);
  const permissionReportsCreate: boolean = getCreatePermission(userInfo, PermissionGroup.Reports);
  const permissionCorrespondenceView: boolean = getViewPermission(userInfo, PermissionGroup.Correspondence);
  const permissionCorrespondenceConfigurationView: boolean = getViewPermission(userInfo, PermissionGroup.CorrespondenceConfiguration);
  const permissionECWizardView = getViewPermission(userInfo, PermissionGroup.ECWizard);
  const permissionECWizardCreate = getCreatePermission(userInfo, PermissionGroup.ECWizard);
  const permissionECWizardEdit = getEditPermission(userInfo, PermissionGroup.ECWizard);

  return (
    <>
      <div className="content__wrapper">
        <UserContext.Provider value={userInfo}>
          <SettingsContextObject.Provider value={settingsContextProviderValue}>
            {
              permissionBulkUploadView
              && (
                <>
                  <Route path="/bulkupload">
                    <BulkUpload
                      gatewayEndPoint={GATEWAY_ENDPOINT}
                      username={username}
                      uploadFilesToS3={uploadFilesToS3}
                      numberUploads={uploads ? uploads.length : 0}
                      maxNumberUploads={1}
                    />
                  </Route>
                  <Route path="/bulkupload-history">
                    <BulkUploadHistory gatewayEndPoint={GATEWAY_ENDPOINT} numberUploads={uploads ? uploads.length : 0} />
                  </Route>
                </>
              )
            }
            {
              permissionCertificatesView
              && (
                <>
                  <Route path="/certificates">
                    <CertificateGrid gatewayEndPoint={GATEWAY_ENDPOINT} />
                  </Route>
                  <Route path="/certificate/add">
                    <AddCertificate gatewayEndPoint={GATEWAY_ENDPOINT} username={username} />
                  </Route>
                </>
              )
            }
            {
              permissionCustomersView
              && (
                <>
                  <Route path="/customers">
                    <CustomerGrid gatewayEndPoint={GATEWAY_ENDPOINT} />
                  </Route>
                </>
              )
            }
            {
              permissionCustomersView
              && (
                <>
                  <Route path="/customer/view/:uuid/:tab">
                    <CustomerInfo gatewayEndPoint={GATEWAY_ENDPOINT} />
                  </Route>
                  <Route path="/customer/edit/:uuid">
                    <CustomerInfo gatewayEndPoint={GATEWAY_ENDPOINT} />
                  </Route>
                  <Route path="/customer/add">
                    <CustomerInfo gatewayEndPoint={GATEWAY_ENDPOINT} />
                  </Route>
                </>
              )
            }
            {
              permissionSettingsView
                && (
                  <>
                    <Route path="/settings/certificate/:tab">
                      <CertificateSettings gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    <Route path="/settings/customer/:tab">
                      <CustomerSettings gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    {permissionSettingsCreate && (
                      <Route path="/settings/taxpayer/add">
                        <TaxPayer gatewayEndPoint={GATEWAY_ENDPOINT} />
                      </Route>
                    )}
                    {permissionSettingsEdit && (
                      <Route path="/settings/taxpayer/edit/:uuid">
                        <TaxPayer gatewayEndPoint={GATEWAY_ENDPOINT} />
                      </Route>
                    )}
                    <Route path="/settings/taxpayers">
                      <TaxPayers gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                  </>
                )
            }
            {
              permissionCorrespondenceView
                && (
                  <>
                    <Route path="/correspondence/templates">
                      <Templates gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    <Route path="/correspondence/subscriptions">
                      <Subscriptions gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                  </>
                )
            }
            {
              permissionCorrespondenceConfigurationView
                && (
                  <>
                    <Route path="/correspondence/configuration/status">
                      <ConfigurationStatus gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    <Route path="/correspondence/configuration/steps">
                      <ConfigurationSteps gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                  </>
                )
            }
            {
              permissionReportsView
                && (
                  <>
                    {permissionReportsCreate && (
                      <Route path="/reports/certificate/add">
                        <CertificateReport gatewayEndPoint={GATEWAY_ENDPOINT} />
                      </Route>
                    )}
                    <Route path="/reports/certificates">
                      <CertificateReports gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    {permissionReportsCreate && (
                    <Route path="/reports/audit/add">
                      <AuditReport gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    )}
                    <Route path="/reports/audits">
                      <AuditReports gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    <Route path="/reports/error-log">
                      <ErrorLog gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                  </>
                )
            }
            {
              permissionECWizardView
                && (
                  <>
                    {
                      permissionECWizardEdit && (
                        <Route path="/ecwizard/settings">
                          <Settings gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    <Route path="/ecwizard/sellers">
                      <Sellers gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    {
                      permissionECWizardCreate && (
                        <Route path="/ecwizard/seller/add">
                          <Seller gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    {
                      permissionECWizardEdit && (
                        <Route path="/ecwizard/seller/edit/:uuid">
                          <Seller gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    {
                      permissionECWizardEdit && (
                        <Route path="/ecwizard/jurisdictions">
                          <Jurisdictions gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    <Route path="/ecwizard/products">
                      <Products gatewayEndPoint={GATEWAY_ENDPOINT} />
                    </Route>
                    {
                      permissionECWizardCreate && (
                        <Route path="/ecwizard/product/add">
                          <Product gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    {
                      permissionECWizardEdit && (
                        <Route path="/ecwizard/product/edit/:uuid">
                          <Product gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                    {
                      permissionECWizardEdit && (
                        <Route path="/ecwizard/exemption-types">
                          <ExemptionTypes gatewayEndPoint={GATEWAY_ENDPOINT} />
                        </Route>
                      )
                    }
                  </>
                )
            }
            {
              permissionCertificatesView
              && (
                <Route path="/" exact strict>
                  <CertificateGrid gatewayEndPoint={GATEWAY_ENDPOINT} />
                </Route>
              )
            }
          </SettingsContextObject.Provider>
        </UserContext.Provider>
      </div>
    </>
  );
});

export default withRouter(Content);
